import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { uploadProfilePicture, deleteProfilePicture } from '../services/profileService';
import './ProfilePictureModal.css';

const ProfilePictureModal = ({ isOpen, onClose, onPictureUpdate }) => {
  const { currentUser } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError('');
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !currentUser) return;

    setUploading(true);
    setError('');

    try {
      const downloadURL = await uploadProfilePicture(currentUser.uid, selectedFile);
      onPictureUpdate(downloadURL);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentUser) return;

    setUploading(true);
    setError('');

    try {
      await deleteProfilePicture(currentUser.uid);
      onPictureUpdate(null);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to delete profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="profile-modal-overlay" onClick={handleClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-modal-header">
          <h3 className="profile-modal-title">Profile Picture</h3>
          <button 
            className="profile-modal-close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        
        <div className="profile-modal-content">
          {error && (
            <div className="profile-modal-error">
              {error}
            </div>
          )}
          
          <div className="profile-modal-preview">
            {preview ? (
              <img 
                src={preview} 
                alt="Preview" 
                className="profile-preview-image"
              />
            ) : (
              <div className="profile-preview-placeholder">
                No image selected
              </div>
            )}
          </div>
          
          <div className="profile-modal-actions">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              style={{ display: 'none' }}
            />
            
            <button
              className="profile-modal-button profile-modal-button-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              Choose Image
            </button>
            
            {selectedFile && (
              <button
                className="profile-modal-button profile-modal-button-secondary"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            )}
            
            <button
              className="profile-modal-button profile-modal-button-danger"
              onClick={handleDelete}
              disabled={uploading}
            >
              {uploading ? 'Deleting...' : 'Remove Picture'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePictureModal;
